import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableCell, TableContainer, TableHead, TableRow, TableBody, Box, TextField, Tooltip, InputAdornment, IconButton, OutlinedInput } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { delimiters } from "../../../../constants/delimiters";
import Split from "react-split";
import hexToAscii from "../../../../utils/utilFunctions";
import LightTooltip from "../../../../commonComponents/LightTooltip";
import CommonGrid from "../../../Repository/CommonGrid";

export const ConstantValueControl = ({ constantValueOptions, constantValue, setConstantValue, onBlur }) => {
  const { t } = useTranslation();
  const constantValueLabel = t("CONSTANT_VALUE");
  const [modalOpen, setModalOpen] = useState(false);
  const [isTextFullScreen, setIsTextFullScreen] = useState(false);
  const textBoxValRef = useRef('');
  const [textBoxVal, setTextBoxVal] = useState('')
  const [textHasNonPrintables, setTextHasNonPrintables] = useState(false);

  const columns = useMemo(() => {
    return [
      { id: 'select', label: t('COL_SELECT'), minWidth: 50, resizable:false },
      { id: 'description', label: t('DESCRIPTION'), minWidth: 150, resizable: true },
      { id: 'hex', label: t('HEX'), minWidth: 50, resizable: false },
    ]
  }, []);

  const handleConstantValueChangeOuter = (e, val) => {
    //Outer, for when user selects from dropdown
    setConstantValue(val || "", true);
  }
  const handleConstantValueChangeInner = (e) => {
    setConstantValue(e?.target?.value || "");
  }

  const handleTextBoxChange = (e) => {
    setTextBoxVal(e.target.value);
  }

  const handleOpen = () => {
    setModalOpen(true);
  }
  const handleClose = () => {
    setModalOpen(false);
    setIsTextFullScreen(false);
  }

  const toggleTextFullScreen = () => {
    setIsTextFullScreen((prev) => !prev);
  };
  const handleApply = () => {
    setConstantValue(textBoxVal);
    setModalOpen(false);
  }
  const handleClear = () => {
    setTextBoxVal("");
  }
  const handleCRLF = () => {
    const input = textBoxValRef.current + `\r\n`
    setTextBoxVal(input);
  }

  const handleRecordDelimiter = () => {
    setTextBoxVal("RECORD DELIMITER");
  }

  const handleAddButton = (hex) => {
    const input = textBoxValRef.current + hexToAscii(hex)
    setTextBoxVal(input);
  }

  const padNumber = (num) => {
    const stringNumber = num.toString();
    return stringNumber.length === 1 ? '0' + num : num;
  }

  const checkForNonPrintables = (text) => {
    let charsFound = false;
    
    text.split("").forEach(e => {
      if (e.charCodeAt() === 10 && !charsFound) charsFound = true;
    });

    return charsFound;
  }

  useEffect(() => {
    if (modalOpen) setTextBoxVal(constantValue);
  }, [modalOpen]);

  useEffect(() => {
    // This ref will be helpful in CommonGrid Function to access value
    textBoxValRef.current = textBoxVal;
  }, [textBoxVal])
  
  return (
    <div className="flex" style={{flex: "1", width: "100%"}}>
      <Autocomplete
        id="fieldConstantValue"
        className="margin-right-4 flex-grow-1"
        size="small"
        freeSolo
        options={constantValueOptions}
        value={constantValue}
        onChange={handleConstantValueChangeOuter}
        style={{minWidth: "150px"}}
        renderInput={(params) => {return (
          <LightTooltip
            id="fieldConstantValueTooltip"
            placement="left"
            title={constantValue}
          >
            <TextField
              {...params}
              size="small"
              label={constantValueLabel}
              value={constantValue}
              onChange={handleConstantValueChangeInner}
              maxRows={4}
              multiline
              onBlur={(e) => {
                if (onBlur) {
                  onBlur(e.target.value); 
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.stopPropagation();
                }
              }}
            />
          </LightTooltip> 
        )}}
      />
      <LightTooltip
        id="fieldConstantCharTableTooltip"
        placement="right"
        title={t('BTN_CHARACTER_TABLE')}
      >
        <Button
          variant={modalOpen ? "contained" : "outlined"}
          color={modalOpen ? "primary" : "inherit"}
          size="small"
          onClick={handleOpen}
          aria-pressed={modalOpen}
          className="muiButtonHeight"
          style={styles.popupBtn}
        >
          {t("BTN_ABC")}
        </Button>
      </LightTooltip>
      

      {/* character map popup - when user clicks `abc` */}
      <Dialog
        onClose={handleClose}
        open={modalOpen}
        fullWidth
        maxWidth={isTextFullScreen ? "xl" : "md"}
        fullScreen={isTextFullScreen}
      >
        <DialogTitle className="dialog-header padding-12" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="title margin-left-4">
            {t('FRG_MSG_SEL_SET') + " " + t("CONSTANT_VALUE")}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <IconButton
              size="small"
              onClick={toggleTextFullScreen}
              aria-label={isTextFullScreen ? "Exit full screen" : "Full screen"}
            >
              {isTextFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          style={{ padding: "0px", fontSize: "12px", fontWeight: "400", height: "100%" }}
          dividers
        >
          {isTextFullScreen ? (
            <div
              style={{
                padding: "16px",
                height: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TextField
                id="constTextBoxFullScreen"
                label={t("CONSTANT_VALUE")}
                multiline
                fullWidth
                value={textBoxVal}
                onChange={handleTextBoxChange}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: "100%",
                    alignItems: "stretch",
                  },
                  "& textarea": {
                    height: "100%",
                    resize: "none",
                  },
                }}
              />
            </div>
          ) : (
            <Split
              style={{ maxHeight: "345px", minHeight: "100px" }}
              className="split width-100 flex"
              direction="horizontal"
              gutterSize={4}
              snapOffset={0}
              dragInterval={4}
              minSize={[50, 50]}
              sizes={[50, 50]}
            >
              <div style={styles.leftContainer}>
                {/* TODO: look into virtualization options. Table is large and causes lag on slow pc every state change. */}
                {modalOpen && <MemoTable 
                  inputColumns={columns} 
                  inputRows={delimiters} 
                  handleAddButton={handleAddButton}
                  padNumber={padNumber}
                />}
              </div>
              <div style={styles.rightContainer}>
                <TextField
                  id="constTextBox"
                  label={t("CONSTANT_VALUE")}
                  multiline
                  rows={14}
                  value={textBoxVal}
                  onChange={handleTextBoxChange}
                  sx={{height: "100%"}}
                  // // inputProps={{sx: {height: "100%"}}}
                  // InputProps={{sx: {height: "100%"}}}
                />
              </div>
            </Split>
          )}

        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCRLF}
            style={styles.popupBtn}
          >
            {t("LBL_CRLF")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRecordDelimiter}
            style={styles.popupBtn}
            // className="margin-right-16"
          >
            {`${t("LBL_CRLF").split(" ")[0]} ${t("RECORD_DELIMITER")}`}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClear}
            style={styles.popupBtn}
            // className="margin-right-16"
          >
            {t("BTN_CLEAR")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleApply}
            style={{...styles.popupBtn, marginRight: "8px"}}
          >
            {t("APPLY")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const MemoTable = memo(({inputColumns, inputRows, handleAddButton, padNumber}) => {
  const { t } = useTranslation();

  const CustomAddButton = (props) => {
		return (
			<Button
				size='small'
				variant='outlined'
				onClick={() => {
					handleAddButton(props.data.data.hex);
				}}
			>
				{t("BTN_ADD")}
			</Button>
		);
	};



  const cols = useMemo(() => inputColumns?.map((column) => (
    {
      headerName: column.label,
      field: column.id,
      resizable: column.resizable,
      width: column.minWidth,
      ...(column.id === "select" ? {
        cellRenderer: CustomAddButton
      } : {})
    }
  )))

  const bodyRows = useMemo(() => inputRows?.map((delimiter, index) => {
    return (
      {
        select:  t('BTN_ADD'),
        description:  `${padNumber(index)}` + ("\xa0").repeat(4) + `${delimiter.display}`,
        hex :  <span>\u00{delimiter.hex}</span>,
        data: delimiter
      }
    )
  }));

  return (
    useMemo(() => {
      return <CommonGrid
        gridOptions={{columnDefs: cols}}
        rowData={bodyRows}
        gridProps={{
          rowHeight: 35
        }}
      />
    },[])
  )
});

const styles = {
  popupBtn: {
    minWidth: "32px",

  },
  leftContainer: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    boxSizing: "border-box",
    padding: "16px",
    minWidth: "150px"
  },
  rightContainer: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    boxSizing: "border-box",
    padding: "16px",
    minWidth: "150px"
  },
}
